/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Component } from 'react';
import type { CSSProperties, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Title } from '@/shared/ui';

interface PublicErrorBoundaryProps {
  children: ReactNode;
}

interface PublicErrorBoundaryState {
  hasError: boolean;
}

const FALLBACK_CONTAINER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  padding: 'var(--space-6)',
  textAlign: 'center',
  color: 'var(--text-primary, #1a1a1a)',
};

const FALLBACK_TITLE_STYLE: CSSProperties = {
  fontSize: '1.25rem',
  marginBottom: 'var(--space-2)',
};

/**
 * Branded fallback shown when the public error boundary catches a render error.
 *
 * Uses the translation hook so its copy is localized; kept as a function
 * component because class error boundaries cannot consume React hooks directly.
 *
 * @returns The localized fallback alert node.
 */
function PublicErrorFallback(): ReactNode {
  const { t } = useTranslation('common');
  return (
    <div style={FALLBACK_CONTAINER_STYLE} role="alert">
      <div>
        <Title order={1} style={FALLBACK_TITLE_STYLE}>
          {t('sharedComponents.publicErrorBoundary.title')}
        </Title>
        <Text style={{ opacity: 0.7 }}>
          {t('sharedComponents.publicErrorBoundary.description')}
        </Text>
      </div>
    </div>
  );
}

/**
 * Error boundary for the public-facing property view.
 *
 * Catches render errors in the public shell and renders a clean, branded
 * fallback instead of crashing to a blank page. Errors are reported to the
 * browser console in development only; no error details (messages, stack
 * traces, component stacks) are sent off-device.
 */
export class PublicErrorBoundary extends Component<
  PublicErrorBoundaryProps,
  PublicErrorBoundaryState
> {
  state: PublicErrorBoundaryState = { hasError: false };

  /**
   * Update state so the next render shows the fallback UI.
   *
   * @returns The next error boundary state.
   */
  static getDerivedStateFromError(): PublicErrorBoundaryState {
    return { hasError: true };
  }

  /**
   * Log the caught error to the console in development environments only.
   *
   * @param error - The error that was thrown.
   * @param info - React component stack information.
   */
  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('PublicErrorBoundary caught an error', error, info);
    }
  }

  /**
   * Render the children, or a fallback message when an error was caught.
   *
   * @returns The rendered node.
   */
  render(): ReactNode {
    if (this.state.hasError) {
      return <PublicErrorFallback />;
    }

    return this.props.children;
  }
}
